/**
 * Layout utilities for managing responsive layout, sidebar, and focus mode
 */

/**
 * Setup CSS custom properties for layout dimensions
 * @param {boolean} isMobile - Whether the current viewport is mobile
 */
export const setupLayoutVariables = (isMobile) => {
    document.documentElement.style.setProperty('--sidebar-width-expanded', '250px');
    document.documentElement.style.setProperty('--sidebar-width-collapsed', '40px');
    document.documentElement.style.setProperty('--navbar-height', '48px');
    document.documentElement.style.setProperty('--footer-height', isMobile ? '50px' : '20px');
  };
  
  /**
   * Get CSS classes for layout elements based on current state
   * @param {boolean} isSidebarOpen - Whether the sidebar is open
   * @param {boolean} isFocusMode - Whether focus mode is active
   * @param {boolean} isMobile - Whether the current viewport is mobile
   * @param {string} focusModeLeftSpace - Left spacing in focus mode
   * @param {string} normalModeLeftSpace - Left spacing in normal mode
   * @param {boolean} sidebarVisible - Whether the sidebar component exists
   * @returns {Object} Object containing CSS classes for layout elements
   */
  export const getLayoutClasses = (isSidebarOpen, isFocusMode, isMobile, focusModeLeftSpace, normalModeLeftSpace, sidebarVisible = true) => {
    setupLayoutVariables(isMobile);
    document.documentElement.style.setProperty('--focus-mode-left-space', focusModeLeftSpace);
    document.documentElement.style.setProperty('--normal-mode-left-space', normalModeLeftSpace);

    const baseClass = "transition-all duration-300";
    const contentContainerClass = `${baseClass} content-container`;
    let mainContentClass = `${baseClass} main-content`;

    // Use global.css tools, no structural overrides
    if (isFocusMode) {
      mainContentClass += " focus-mode";
    } else {
      // Only add sidebar-open class if sidebar is visible AND open
      if (sidebarVisible && isSidebarOpen) {
        mainContentClass += " sidebar-open";
      }

      // Always add mobile-view class on mobile devices
      if (isMobile) {
        mainContentClass += " mobile-view";
      }
    }

    return {
      contentContainer: contentContainerClass,
      mainContent: mainContentClass
    };
  };
  
  /**
   * Detect device orientation
   * @returns {string} 'landscape' or 'portrait'
   */
  export const detectOrientation = () => {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  };
  
  /**
   * Detect if device is a touch device (phones, tablets)
   * @returns {boolean} True if touch device
   */
  export const detectTouchDevice = () => {
    // Check multiple methods for maximum compatibility
    const hasTouch = 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches;

    // iPadOS 13+ reports as Mac but has touch - check specifically
    // eslint-disable-next-line
    const isIPad = navigator.maxTouchPoints > 1 ||
      /iPad/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && 'ontouchend' in document);

    return hasTouch || isIPad;
  };

  /**
   * Detect if current viewport is tablet (iPad, Android tablet, etc.)
   * @returns {boolean} True if viewport is tablet
   */
  export const detectTablet = () => {
    const width = window.innerWidth;
    const isTouch = detectTouchDevice();

    // iPad specific detection (iPadOS 13+ pretends to be Mac)
    // eslint-disable-next-line
    const isIPad = /iPad/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
      (navigator.platform === 'MacIntel' && 'ontouchend' in document);

    // Tablet: touch device with width >= 700px (iPad Mini is 744px) and <= 1366px (not large desktop)
    // OR explicitly detected as iPad
    return isIPad || (isTouch && width >= 700 && width <= 1366);
  };

  /**
   * Detect if current viewport is mobile phone (not tablet)
   * iPad Mini is 744px, so use 700px threshold
   * @returns {boolean} True if viewport is mobile phone
   */
  export const detectMobile = () => {
    return window.innerWidth < 700;
  };

  /**
   * Detect if device needs touch-friendly UI (phones + tablets)
   * Use this for navbar hide/show, touch gestures, etc.
   * @returns {boolean} True if device needs touch UI
   */
  export const detectMobileOrTablet = () => {
    return detectMobile() || detectTablet();
  };